/**
 * Country phone-dialing codes + helpers for the ISR workspace's phone editor.
 *
 * Single source of truth for:
 *  - ISO → international dialing prefix (COUNTRY_DIALING_CODES)
 *  - ISO → flag emoji (getCountryFlagEmoji)
 *  - lead country resolution (resolveLeadCountryCode / leadDialingCode)
 *  - phone presence-of-country-code checks (hasCountryCode)
 *  - per-country phone number validation & line-type detection (classifyPhoneNumber)
 *
 * The CountryPhoneSelect atom (components/atoms/CountryPhoneSelect.tsx) and the
 * ISR workspace both consume this module so the data stays in one place.
 */

/** ISO country code → international dialing prefix. Kept to the most common
 *  markets; anything not listed resolves to ''. */
export const COUNTRY_DIALING_CODES: Record<string, string> = {
  AD: '+376', AE: '+971', AF: '+93', AG: '+1268', AI: '+1264', AL: '+355', AM: '+374',
  AO: '+244', AR: '+54', AS: '+1684', AT: '+43', AU: '+61', AW: '+297', AZ: '+994',
  BA: '+387', BB: '+1246', BD: '+880', BE: '+32', BF: '+226', BG: '+359', BH: '+973',
  BI: '+257', BJ: '+229', BM: '+1441', BN: '+673', BO: '+591', BR: '+55', BS: '+1242',
  BT: '+975', BW: '+267', BY: '+375', BZ: '+501', CA: '+1', CD: '+243', CF: '+236',
  CG: '+242', CH: '+41', CI: '+225', CK: '+682', CL: '+56', CM: '+237', CN: '+86',
  CO: '+57', CR: '+506', CU: '+53', CV: '+238', CY: '+357', CZ: '+420', DE: '+49',
  DJ: '+253', DK: '+45', DM: '+1767', DO: '+1809', DZ: '+213', EC: '+593', EE: '+372',
  EG: '+20', ER: '+291', ES: '+34', ET: '+251', FI: '+358', FJ: '+679', FK: '+500',
  FM: '+691', FO: '+298', FR: '+33', GA: '+241', GB: '+44', GD: '+1473', GE: '+995',
  GH: '+233', GI: '+350', GL: '+299', GM: '+220', GN: '+224', GQ: '+240', GR: '+30',
  GT: '+502', GU: '+1671', GW: '+245', GY: '+592', HK: '+852', HN: '+504', HR: '+385',
  HT: '+509', HU: '+36', ID: '+62', IE: '+353', IL: '+972', IN: '+91', IQ: '+964',
  IR: '+98', IS: '+354', IT: '+39', JM: '+1876', JO: '+962', JP: '+81', KE: '+254',
  KG: '+996', KH: '+855', KI: '+686', KM: '+269', KN: '+1869', KP: '+850', KR: '+82',
  KW: '+965', KY: '+1345', KZ: '+7', LA: '+856', LB: '+961', LC: '+1758', LI: '+423',
  LK: '+94', LR: '+231', LS: '+266', LT: '+370', LU: '+352', LV: '+371', LY: '+218',
  MA: '+212', MC: '+377', MD: '+373', ME: '+382', MG: '+261', MH: '+692', MK: '+389',
  ML: '+223', MM: '+95', MN: '+976', MO: '+853', MP: '+1670', MR: '+222', MS: '+1664',
  MT: '+356', MU: '+230', MV: '+960', MW: '+265', MX: '+52', MY: '+60', MZ: '+258',
  NA: '+264', NC: '+687', NE: '+227', NG: '+234', NI: '+505', NL: '+31', NO: '+47',
  NP: '+977', NR: '+674', NZ: '+64', OM: '+968', PA: '+507', PE: '+51', PF: '+689',
  PG: '+675', PH: '+63', PK: '+92', PL: '+48', PM: '+508', PR: '+1787', PT: '+351',
  PW: '+680', PY: '+595', QA: '+974', RO: '+40', RS: '+381', RU: '+7', RW: '+250',
  SA: '+966', SB: '+677', SC: '+248', SD: '+249', SE: '+46', SG: '+65', SH: '+290',
  SI: '+386', SK: '+421', SL: '+232', SM: '+378', SN: '+221', SO: '+252', SR: '+597',
  ST: '+239', SV: '+503', SY: '+963', SZ: '+268', TC: '+1649', TD: '+235', TG: '+228',
  TH: '+66', TJ: '+992', TM: '+993', TN: '+216', TO: '+676', TR: '+90', TT: '+1868',
  TV: '+688', TZ: '+255', UA: '+380', UG: '+256', US: '+1', UK: '+44', UY: '+598',
  UZ: '+998', VC: '+1784', VE: '+58', VG: '+1284', VI: '+1340', VN: '+84', VU: '+678',
  WS: '+685', YE: '+967', ZA: '+27', ZM: '+260', ZW: '+263',
};

/** ISO country code → flag emoji, derived programmatically from the regional
 *  indicator symbols so every code works without a lookup table. */
export const getCountryFlagEmoji = (iso2: string): string => {
  const code = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '';
  // Regional indicator symbol: U+1F1E6 = 'A' … U+1F1E6+25 = 'Z'.
  const base = 0x1f1e6;
  return String.fromCodePoint(base + code.charCodeAt(0) - 65, base + code.charCodeAt(1) - 65);
};

/** Country name (English) for an ISO code, or the code itself when unknown. */
export const countryName = (iso2: string): string => {
  const code = iso2.trim().toUpperCase();
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    if (name && name !== code) return name;
  } catch { /* fall through */ }
  return code;
};

/** True when a phone string already starts with a +country code. */
export const hasCountryCode = (value: string): boolean =>
  /^\+[1-9]\d{0,3}/.test(value.trim());

/** Resolve the lead's ISO country code (ES, US, …) from any country-ish field
 *  on the lead. Accepts a raw ISO code or a full country NAME (Spain, España),
 *  and falls back through company country, state/region, then person. */
export const resolveLeadCountryCode = (lead: {
  companyCountry?: string | null;
  companyState?: string | null;
  companyLvl1?: Record<string, any> | null;
  personLvl1?: Record<string, any> | null;
  rawOriginalData?: Record<string, any> | null;
}): string => {
  const raw = lead.rawOriginalData || {};
  const company = (lead.companyLvl1 || raw.lvl1_company || {}) as Record<string, any>;
  const person = (lead.personLvl1 || raw.lvl1_person || {}) as Record<string, any>;

  const candidates = [
    lead.companyCountry,
    company.country_code,
    company.country,
    lead.companyState,
    company.state,
    person.country_code,
    person.country,
  ];
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (!value || value === 'Unknown') continue;
    // Direct 2-letter ISO code.
    if (/^[A-Z]{2}$/i.test(value)) return value.toUpperCase();
    // Full country name → ISO code via the browser's locale data.
    try {
      const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
      for (const iso of Object.keys(COUNTRY_DIALING_CODES)) {
        if (displayNames.of(iso)?.toLowerCase() === value.toLowerCase()) {
          return iso;
        }
      }
    } catch { /* locale data unavailable */ }
  }
  return '';
};

/** Dialing prefix ('+34') for the lead's country, or '' when unknown. */
export const leadDialingCode = (lead: {
  companyCountry?: string | null;
  companyState?: string | null;
  companyLvl1?: Record<string, any> | null;
  personLvl1?: Record<string, any> | null;
  rawOriginalData?: Record<string, any> | null;
}): string => {
  const code = resolveLeadCountryCode(lead);
  return code ? COUNTRY_DIALING_CODES[code] || '' : '';
};

/** Split a phone value into its +country-code prefix and the remainder. */
export const splitPhonePrefix = (full: string): { prefix: string; rest: string } => {
  const trimmed = (full || '').trim();
  if (!trimmed.startsWith('+')) return { prefix: '', rest: trimmed };

  // Try every known dialing code, longest-first, so +33 wins over +331.
  // The phone number after the match is the rest.
  const knownCodes = Object.values(COUNTRY_DIALING_CODES).sort((a, b) => b.length - a.length);
  for (const code of knownCodes) {
    if (trimmed.startsWith(code)) {
      const rest = trimmed.slice(code.length).replace(/^[\s-]+/, '');
      return { prefix: code, rest };
    }
  }
  // Fallback: grab the first 1-4 digits as a generic prefix.
  const match = trimmed.match(/^(\+\d{1,4})\s*(.*)$/);
  if (match) return { prefix: match[1], rest: match[2] };
  return { prefix: '', rest: trimmed };
};

// ─── Phone number validation & line-type detection ────────────────────────
// Per-country patterns for the first digits after the country code that
// distinguish mobile vs landline numbers. This is a curated, maintainable
// database — not exhaustive for every country, but covers the markets our
// leads operate in. Extend as needed.
//
// References: ITU national numbering plans, Wikipedia.
// Format: dialingCode → { mobile: string[], landline: string[], length: number }

export type PhoneLineType = 'mobile' | 'landline' | 'unknown';

interface CountryPhonePattern {
  /** Mobile prefixes — first digits of the national number (after country code). */
  mobile: string[];
  /** Landline prefixes — first digits of the national number. */
  landline: string[];
  /**
   * Expected total length of the national number (digits after country code),
   * or a range [min, max]. When set, numbers outside this range are INVALID.
   * When absent, length is not enforced.
   */
  length?: number | [number, number];
}

const PHONE_PATTERNS: Record<string, CountryPhonePattern> = {
  // ── Spain ────────────────────────────────────────────────────────────────
  // Mobile: 6XX, 7XX (some 7 are landline but 71-74 are VoIP, 7[0-9] generally mobile)
  // Landline: 9XX, 8XX, 5XX
  // National length: 9 digits
  ES: {
    mobile: ['6', '7'],
    landline: ['9', '8', '5'],
    length: 9,
  },
  // ── France ───────────────────────────────────────────────────────────────
  // Mobile: 06, 07
  // Landline: 01-05, 08, 09
  // National length: 9 digits (after +33)
  FR: {
    mobile: ['6', '7'],
    landline: ['1', '2', '3', '4', '5', '8', '9'],
    length: 9,
  },
  // ── UK ───────────────────────────────────────────────────────────────────
  // Mobile: 07XXX
  // Landline: 01, 02, 03
  // National length: 10 digits (most), 9 or 11 for some
  GB: {
    mobile: ['7'],
    landline: ['1', '2', '3'],
    length: [9, 11],
  },
  // ── Germany ──────────────────────────────────────────────────────────────
  // Mobile: 015, 016, 017, 018 (some 018 are landline)
  // Landline: 02XX, 03XX, 04XX, etc.
  DE: {
    mobile: ['15', '16', '17'],
    landline: ['2', '3', '4', '5', '6', '7', '8', '9'],
    length: [6, 13],
  },
  // ── Italy ────────────────────────────────────────────────────────────────
  // Mobile: 3XX
  // Landline: 0[1-9] (excluding 3)
  IT: {
    mobile: ['3'],
    landline: ['0', '1', '2', '4', '5', '6', '7', '8', '9'],
    length: [8, 11],
  },
  // ── Portugal ─────────────────────────────────────────────────────────────
  // Mobile: 9X, 93
  PT: {
    mobile: ['9'],
    landline: ['2', '3', '4', '5', '6', '7', '8'],
    length: 9,
  },
  // ── Netherlands ──────────────────────────────────────────────────────────
  NL: {
    mobile: ['6'],
    landline: ['1', '2', '3', '4', '5', '7', '8', '9'],
    length: 9,
  },
  // ── Belgium ──────────────────────────────────────────────────────────────
  BE: {
    mobile: ['4'],
    landline: ['1', '2', '3', '5', '6', '7', '8', '9'],
    length: [8, 9],
  },
  // ── Switzerland ──────────────────────────────────────────────────────────
  CH: {
    mobile: ['7'],
    landline: ['2', '3', '4', '5', '6', '8', '9'],
    length: 9,
  },
  // ── Sweden ───────────────────────────────────────────────────────────────
  SE: {
    mobile: ['7'],
    landline: ['1', '2', '3', '4', '5', '6', '8', '9'],
    length: [7, 13],
  },
  // ── Norway ───────────────────────────────────────────────────────────────
  NO: {
    mobile: ['4', '9'],
    landline: ['2', '3', '5', '6', '7', '8'],
    length: 8,
  },
  // ── Denmark ──────────────────────────────────────────────────────────────
  DK: {
    mobile: ['2', '3', '4', '5', '6', '7', '8', '9'],
    // No clear landline prefix — rely on length
    landline: [],
    length: 8,
  },
  // ── Finland ──────────────────────────────────────────────────────────────
  FI: {
    mobile: ['4', '5'],
    landline: ['1', '2', '3', '6', '7', '8', '9'],
    length: [8, 12],
  },
  // ── Ireland ──────────────────────────────────────────────────────────────
  IE: {
    mobile: ['8'],
    landline: ['1', '2', '3', '4', '5', '6', '7', '9'],
    length: [7, 10],
  },
  // ── Austria ──────────────────────────────────────────────────────────────
  AT: {
    mobile: ['6'],
    landline: ['1', '2', '3', '4', '5', '7', '8', '9'],
    length: [7, 13],
  },
  // ── Poland ───────────────────────────────────────────────────────────────
  PL: {
    mobile: ['4', '5', '6', '7', '8'],
    landline: ['1', '2', '3', '9'],
    length: 9,
  },
  // ── Czech Republic ───────────────────────────────────────────────────────
  CZ: {
    mobile: ['6', '7'],
    landline: ['2', '3', '4', '5', '8', '9'],
    length: 9,
  },
  // ── US / Canada ──────────────────────────────────────────────────────────
  // Mobile vs landline is hard to distinguish by prefix alone (number portability).
  // We treat +1 numbers as "mobile-compatible" (both types accepted everywhere).
  US: { mobile: ['2', '3', '4', '5', '6', '7', '8', '9'], landline: [], length: 10 },
  CA: { mobile: ['2', '3', '4', '5', '6', '7', '8', '9'], landline: [], length: 10 },
  // ── Mexico ───────────────────────────────────────────────────────────────
  MX: {
    mobile: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    landline: [],
    length: 10,
  },
  // ── Argentina ────────────────────────────────────────────────────────────
  AR: {
    mobile: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    landline: [],
    length: 10,
  },
  // ── Brazil ───────────────────────────────────────────────────────────────
  BR: {
    mobile: ['9', '8', '7', '6', '5'],
    // 9XXX = mobile, others = landline, but portability blurs it
    landline: ['1', '2', '3', '4'],
    length: [10, 11],
  },
  // ── Colombia ─────────────────────────────────────────────────────────────
  CO: {
    mobile: ['3'],
    landline: ['1', '2', '4', '5', '6', '7', '8'],
    length: 10,
  },
  // ── Chile ────────────────────────────────────────────────────────────────
  CL: {
    mobile: ['9'],
    landline: ['2', '3', '4', '5', '6', '7', '8'],
    length: 9,
  },
  // ── India ────────────────────────────────────────────────────────────────
  IN: {
    mobile: ['6', '7', '8', '9'],
    landline: ['1', '2', '3', '4', '5'],
    length: 10,
  },
  // ── China ────────────────────────────────────────────────────────────────
  CN: {
    mobile: ['1'],
    landline: ['2', '3', '4', '5', '6', '7', '8', '9'],
    length: [10, 11],
  },
  // ── Japan ────────────────────────────────────────────────────────────────
  JP: {
    mobile: ['7', '8', '9'],
    landline: ['1', '2', '3', '4', '5', '6'],
    length: [8, 11],
  },
  // ── Australia ────────────────────────────────────────────────────────────
  AU: {
    mobile: ['4'],
    landline: ['2', '3', '7', '8'],
    length: 9,
  },
  // ── Russia ───────────────────────────────────────────────────────────────
  RU: {
    mobile: ['9'],
    landline: ['4', '8', '3', '2', '1', '5', '6', '7'],
    length: 10,
  },
};

/** Resolve the ISO2 country code from a dialing prefix (e.g. '+33' → 'FR'). */
const dialingCodeToISO = (dialing: string): string | null => {
  const entry = Object.entries(COUNTRY_DIALING_CODES).find(([, code]) => code === dialing);
  return entry ? entry[0] : null;
};

/** Strip all non-digit characters and return the bare national number. */
const bareNationalNumber = (full: string): string => {
  const { prefix, rest } = splitPhonePrefix(full);
  return rest.replace(/\D/g, '');
};

/**
 * Result of classifying a phone number.
 */
export interface PhoneClassification {
  type: PhoneLineType;
  /** ISO2 country code when the prefix was identified. */
  iso2: string | null;
  /** Human-readable description of the detected type. */
  label: string;
  /** True when the number looks structurally valid (length, prefix). */
  valid: boolean;
  /** Error message when invalid, or null. */
  error: string | null;
}

/**
 * Classify a phone number by its country code and the first digits of the
 * national number. Returns the line type (mobile / landline / unknown) and
 * whether the number is structurally valid.
 *
 * @example
 *   classifyPhoneNumber('+34 612 345 678')
 *   // → { type: 'mobile', iso2: 'ES', label: 'Spanish mobile', valid: true, error: null }
 *
 *   classifyPhoneNumber('+34 912 345 678')
 *   // → { type: 'landline', iso2: 'ES', label: 'Spanish landline', valid: true, error: null }
 *
 *   classifyPhoneNumber('+34 512 345 678')
 *   // → { type: 'unknown', iso2: 'ES', label: 'Unknown line type', valid: true, error: null }
 */
export const classifyPhoneNumber = (full: string): PhoneClassification => {
  const { prefix } = splitPhonePrefix(full);
  if (!prefix) return { type: 'unknown', iso2: null, label: 'Missing country code', valid: false, error: 'Add the country code (e.g. +34) before the number.' };

  const iso2 = dialingCodeToISO(prefix);
  if (!iso2) return { type: 'unknown', iso2: null, label: 'Unknown country', valid: true, error: null };

  const pattern = PHONE_PATTERNS[iso2];
  if (!pattern) return { type: 'unknown', iso2, label: `${iso2} number`, valid: true, error: null };

  const national = bareNationalNumber(full);
  const nationalDigits = national.replace(/\D/g, '');

  // Length check
  if (pattern.length !== undefined) {
    const len = nationalDigits.length;
    const range = Array.isArray(pattern.length) ? pattern.length : [pattern.length, pattern.length];
    if (len < range[0] || len > range[1]) {
      return {
        type: 'unknown',
        iso2,
        label: `${iso2} number`,
        valid: false,
        error: `Invalid ${iso2} number: expected ${range[0]}-${range[1]} digits, got ${len}.`,
      };
    }
  }

  // Determine line type by comparing the first digit(s) of the national number.
  const firstDigit = nationalDigits[0] || '';
  const firstTwo = nationalDigits.slice(0, 2);

  // Check mobile prefixes (longest match first).
  for (const mobilePrefix of pattern.mobile) {
    if (mobilePrefix.length === 2 && firstTwo === mobilePrefix) {
      return { type: 'mobile', iso2, label: `${iso2} mobile`, valid: true, error: null };
    }
  }
  for (const mobilePrefix of pattern.mobile) {
    if (mobilePrefix.length === 1 && firstDigit === mobilePrefix) {
      // Ensure a 2-char mobile prefix didn't match as a 1-char (e.g. DE '15' vs '1').
      const longerMatch = pattern.mobile.some(p => p.length === 2 && p.startsWith(mobilePrefix) && firstTwo === p);
      if (!longerMatch) {
        return { type: 'mobile', iso2, label: `${iso2} mobile`, valid: true, error: null };
      }
    }
  }

  // Check landline prefixes.
  for (const landlinePrefix of pattern.landline) {
    if (landlinePrefix.length === 2 && firstTwo === landlinePrefix) {
      return { type: 'landline', iso2, label: `${iso2} landline`, valid: true, error: null };
    }
  }
  for (const landlinePrefix of pattern.landline) {
    if (landlinePrefix.length === 1 && firstDigit === landlinePrefix) {
      return { type: 'landline', iso2, label: `${iso2} landline`, valid: true, error: null };
    }
  }

  return { type: 'unknown', iso2, label: `${iso2} number`, valid: true, error: null };
};

/**
 * Validate a phone number for a specific field type.
 * - whatsapp_id / mobile_id: must be MOBILE
 * - phone_id: can be MOBILE or LANDLINE (but must be valid)
 *
 * Returns a human-readable error message, or null when valid.
 */
export const validatePhoneForField = (
  full: string,
  fieldKey: 'phone_id' | 'mobile_id' | 'whatsapp_id',
): { blocker: boolean; message: string | null } => {
  if (!full.trim()) return { blocker: false, message: null };

  const classification = classifyPhoneNumber(full);

  // Structural validity (length, country code) always blocks.
  if (!classification.valid) return { blocker: true, message: classification.error };

  // Line-type enforcement for mobile-only fields.
  if (fieldKey === 'mobile_id' || fieldKey === 'whatsapp_id') {
    if (classification.type === 'landline') {
      return {
        blocker: true,
        message: `This field requires a mobile number. "${classification.label}" numbers are not accepted.`,
      };
    }
    if (classification.type === 'unknown') {
      return {
        blocker: false,
        message: `Could not verify this is a mobile number. Check the number format.`,
      };
    }
  }

  return { blocker: false, message: null };
};
